import React, { useEffect, useRef, useState } from 'react';
import { vendorService } from '../../services';
import { useDialog } from '../../context/DialogContext';
import TintSquare from '../hand/TintSquare';
import { SearchIcon } from '../hand/icons';
import { dayMonthDay, money } from './vendor/vendorUi';
import { LISTED_GRAY, MUTED } from './proUi';
import { TagPill, PillButton, INSET, CHIP, SUBTLE, RULE, CYAN } from '../club/clubUi';
import { dayLabel } from '../../utils/surfaceFormat';

/**
 * ProEventPicker — EVENTS ON YOUR PAGE (docs/plans/2026-09-pro-multipage-events).
 *
 * A search-and-pick over every event a page may link to, drawn the way the top bar's
 * search is: a 2px box with the search glyph, then rows of a 34px poster square, the
 * title and `DAY · CITY`. The caller's own events come first (`YOURS`); anything already
 * on the page says `LINKED`. A click links immediately — the row lands in the LINKED list
 * below with REMOVE — and an empty input shows the caller's own unlinked events.
 *
 * The parent owns `linked` (rows from `vendorService.pageEvents`). `onLinked(row)` must
 * upsert by id (the optimistic row is replaced by the server's answer); `onUnlinked(id)`
 * removes. On a refusal the picker reverts and says why through `useDialog`.
 */

/**
 * Status pill shared with the venue events block: SELLING / FUNDING / ENDED / CANCELED / raw.
 * A still-"selling" row whose date has passed reads ENDED too — the status job trails the
 * clock by a few hours, and the owner needs to see at a glance that it's over.
 */
export function eventStatusPill(status, dateTime) {
  const s = (status || '').toLowerCase();
  const past = !!dateTime && new Date(dateTime).getTime() < Date.now();
  const pill = (bg, fg, text, ring) => <TagPill bg={bg} fg={fg} ring={ring} size={8} padding="4px 10px">{text}</TagPill>;
  if (s === 'canceled' || s === 'cancelled') return pill('#fff', MUTED, 'CANCELED', LISTED_GRAY);
  if (s === 'dissolved') return pill('#fff', MUTED, 'DISSOLVED', LISTED_GRAY);
  if (s === 'ended' || (past && (s === 'selling' || s === 'greenlit' || s === 'live' || s === 'funding'))) return pill('#E5E7EB', '#000', 'ENDED');
  if (s === 'selling' || s === 'greenlit' || s === 'live') return pill('#22C55E', '#fff', 'SELLING');
  if (s === 'funding') return pill('#EAB308', '#000', 'FUNDING');
  return pill('#fff', MUTED, (status || 'DRAFT').toUpperCase(), LISTED_GRAY);
}

const metaLine = (e) => [dayLabel(e.dateTime), e.locationCity || e.venueName].filter(Boolean).join(' · ');

export default function ProEventPicker({ pageId, linked = [], onLinked, onUnlinked, autoFocus = false }) {
  const { showAlert } = useDialog();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(null); // event id mid-flight
  const reqId = useRef(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const linkedIds = new Set((linked || []).map((e) => e.id));
  const isLinked = (row) => !!row.isLinked || linkedIds.has(row.id);

  // Debounced suggest, newest keystroke wins. Runs on open too, so an empty box shows
  // the caller's own unlinked events straight away.
  useEffect(() => {
    if (!open || !pageId) return undefined;
    const q = query.trim();
    const t = setTimeout(async () => {
      const id = ++reqId.current;
      setSearching(true);
      const r = await vendorService.suggestPageEvents(pageId, q, 20);
      if (id !== reqId.current) return;
      setSearching(false);
      setResults(r.success ? (r.data?.items || r.data || []) : []);
      setActive(-1);
    }, 150);
    return () => clearTimeout(t);
  }, [query, open, pageId]);

  // Keep the keyboard cursor in view.
  useEffect(() => {
    if (active < 0 || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const link = async (row) => {
    if (!row || isLinked(row) || busy) return;
    const optimistic = { ...row, isLinked: true };
    setBusy(row.id);
    onLinked?.(optimistic);
    setResults((rs) => rs.map((x) => (x.id === row.id ? optimistic : x)));
    const r = await vendorService.linkPageEvent(pageId, row.id);
    setBusy(null);
    if (r.success) {
      if (r.data && r.data.id) onLinked?.({ ...row, ...r.data, isLinked: true });
      return;
    }
    onUnlinked?.(row.id);
    setResults((rs) => rs.map((x) => (x.id === row.id ? { ...x, isLinked: false } : x)));
    await showAlert({ title: 'LINK AN EVENT', message: r.error || 'That event could not be linked.', type: 'error' });
  };

  const unlink = async (row) => {
    if (busy) return;
    setBusy(row.id);
    onUnlinked?.(row.id);
    setResults((rs) => rs.map((x) => (x.id === row.id ? { ...x, isLinked: false } : x)));
    const r = await vendorService.unlinkPageEvent(pageId, row.id);
    setBusy(null);
    if (r.success) return;
    onLinked?.(row);
    setResults((rs) => rs.map((x) => (x.id === row.id ? { ...x, isLinked: true } : x)));
    await showAlert({ title: 'REMOVE AN EVENT', message: r.error || 'That event could not be removed.', type: 'error' });
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (results.length ? (a + 1) % results.length : -1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (results.length ? (a <= 0 ? results.length - 1 : a - 1) : -1)); }
    else if (e.key === 'Enter') { if (active >= 0 && results[active]) { e.preventDefault(); link(results[active]); } }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  const q = query.trim();
  const showList = open && !!pageId;

  return (
    <div className="flex flex-col font-mono" style={{ gap: 12 }} data-testid="pro-event-picker">
      <div className="relative">
        <div className="flex items-center" style={{ gap: 10, height: 48, padding: '0 8px 0 18px', borderRadius: 999, background: INSET }}>
          <span className="flex-none text-black"><SearchIcon size={15} /></span>
          <input
            ref={inputRef}
            value={query}
            autoFocus={autoFocus}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={onKeyDown}
            placeholder="SEARCH EVENTS — YOURS COME FIRST"
            aria-label="Search events to put on this page"
            aria-expanded={showList}
            aria-controls="pro-event-suggestions"
            aria-autocomplete="list"
            role="combobox"
            className="min-w-0 flex-1 bg-transparent font-mono uppercase text-black outline-none focus:ring-0 placeholder-gray-400"
            style={{ fontSize: 12, letterSpacing: '0.08em', border: 'none', padding: 0 }}
          />
          {q && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="flex-none font-mono font-bold uppercase" style={{ height: 32, padding: '0 12px', border: 'none', borderRadius: 999, background: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>CLEAR</button>}
        </div>
        {showList && (
          <div className="absolute inset-x-0 top-full z-20 max-h-80 overflow-y-auto bg-white" style={{ marginTop: 6, borderRadius: 20, boxShadow: 'inset 0 0 0 2px #000' }} role="listbox" id="pro-event-suggestions" ref={listRef} data-testid="event-suggestions">
            {searching && results.length === 0 ? (
              <p className="px-3 py-3 font-mono uppercase" style={{ fontSize: 11, letterSpacing: '0.05em', color: LISTED_GRAY }}>Searching…</p>
            ) : results.length === 0 ? (
              <p className="px-3 py-3 font-mono uppercase" style={{ fontSize: 11, letterSpacing: '0.05em', color: LISTED_GRAY }}>{q ? 'No events match.' : 'Nothing of yours left to add — search for any event.'}</p>
            ) : results.map((item, i) => {
              const done = isLinked(item);
              const hot = i === active;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={hot}
                  aria-disabled={done}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); if (!done) link(item); }}
                  className={`flex w-full items-center gap-3 text-left ${done ? 'cursor-default' : ''}`}
                  style={{ padding: '10px 14px', border: 0, borderTop: i ? RULE : 'none', background: hot && !done ? INSET : '#fff', color: '#000', opacity: done ? 0.55 : 1, fontFamily: 'inherit', cursor: done ? 'default' : 'pointer' }}
                  data-testid={`suggest-${item.id}`}
                >
                  <TintSquare name={item.title} imageUrl={item.posterUrl} size={40} tintKey={item.id} style={{ border: 'none', borderRadius: 12, overflow: 'hidden' }} />
                  <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                    <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{item.title}</span>
                    <span className="truncate font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: MUTED }}>{metaLine(item)}{item.hostName && !item.isMine ? ` · ${item.hostName}` : ''}</span>
                  </span>
                  <span className="flex-none" data-testid={`suggest-status-${item.id}`}>{eventStatusPill(item.status, item.dateTime)}</span>
                  {item.isMine && <TagPill bg="#000" fg="#fff" size={8} padding="3px 8px">YOURS</TagPill>}
                  <TagPill bg={done ? CHIP : CYAN} fg="#000" size={8} padding="3px 8px">{done ? 'LINKED' : 'LINK'}</TagPill>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col bg-white" data-testid="linked-events">
        <div className="flex items-center justify-between" style={{ padding: '4px 0 6px' }}>
          <span className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em' }}>LINKED · {(linked || []).length}</span>
          <span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.14em', color: SUBTLE }}>SHOWS ON YOUR EVENTS TAB</span>
        </div>
        {(linked || []).length === 0 && <div className="font-mono" style={{ padding: '16px 18px', borderRadius: 18, background: INSET, fontSize: 12, color: MUTED }}>Nothing linked yet. Search above — your own events are suggested first.</div>}
        {(linked || []).map((e, i) => (
          <div key={e.id} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '10px 0', borderTop: i === 0 ? 'none' : RULE }} data-testid={`linked-${e.id}`}>
            <span className="flex-none overflow-hidden" style={{ width: 52, height: 52, borderRadius: 14, background: INSET }} aria-hidden>
              {e.posterUrl ? <img src={e.posterUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : <TintSquare name={e.title} size={52} tintKey={e.id} style={{ border: 'none', width: '100%', height: '100%' }} />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col" style={{ flex: '1 1 200px', gap: 4 }}>
              <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{e.title}</span>
              <span className="flex flex-wrap items-center font-mono uppercase" style={{ gap: 8, fontSize: 9, letterSpacing: '0.1em', color: MUTED }}>
                <span>{dayMonthDay(e.dateTime)}</span>
                {e.priceCents != null && <span>{e.priceCents > 0 ? money(e.priceCents) : 'FREE'}</span>}
                {e.isMine ? <span>YOURS</span> : e.hostName ? <span>HOSTED BY {e.hostName}</span> : null}
              </span>
            </span>
            {eventStatusPill(e.status, e.dateTime)}
            <PillButton variant="tertiary" size={32} onClick={() => unlink(e)} disabled={busy === e.id} style={{ opacity: busy === e.id ? 0.5 : 1 }} aria-label={`Remove ${e.title}`}>REMOVE</PillButton>
          </div>
        ))}
      </div>
    </div>
  );
}
